import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { usersAPI, kpisAPI, teamsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Key,
  Users,
  Target,
  UsersRound,
  Eye,
  Edit,
  Shield,
  UserCog,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../lib/utils';

const Permissions = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [teams, setTeams] = useState([]);
  const [viewMode, setViewMode] = useState('users'); // 'users' | 'kpis'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, kpisRes, teamsRes] = await Promise.all([
        usersAPI.getAll(),
        kpisAPI.getAll(),
        teamsAPI.getAll(),
      ]);
      setUsers(usersRes.data);
      setKpis(kpisRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get role badge color
  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-red-100 text-red-700">Admin</Badge>;
      case 'viewer':
        return <Badge className="bg-slate-100 text-slate-700">Viewer</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-700">Operador</Badge>;
    }
  };

  // Get teams for a user
  const getUserTeams = (userId) => {
    return teams.filter(team => team.member_ids?.includes(userId));
  };

  // Get KPIs accessible by a user
  const getUserKpis = (user) => {
    if (user.role === 'admin') {
      return kpis; // Admin sees all
    }
    if (user.role === 'viewer') {
      return kpis.filter(kpi => user.assigned_kpi_ids?.includes(kpi.id));
    }
    // Regular user - access through teams
    const userTeamIds = getUserTeams(user.id).map(t => t.id);
    return kpis.filter(kpi => 
      kpi.team_ids?.some(teamId => userTeamIds.includes(teamId))
    );
  };

  // Get users who can access a KPI
  const getKpiUsers = (kpi) => {
    const accessibleUsers = [];
    
    users.forEach(user => {
      if (user.role === 'admin') {
        accessibleUsers.push({ user, accessType: 'admin' });
      } else if (user.role === 'viewer' && user.assigned_kpi_ids?.includes(kpi.id)) {
        accessibleUsers.push({ user, accessType: 'assigned' });
      } else if (user.role === 'user') {
        const userTeamIds = getUserTeams(user.id).map(t => t.id);
        if (kpi.team_ids?.some(teamId => userTeamIds.includes(teamId))) {
          accessibleUsers.push({ user, accessType: 'team' });
        }
      }
    });
    
    return accessibleUsers;
  };

  // Get teams that have access to a KPI
  const getKpiTeams = (kpi) => {
    return teams.filter(team => kpi.team_ids?.includes(team.id));
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Matriz de Permisos
          </h1>
          <p className="text-slate-500 mt-1">
            Visualiza qué usuarios tienen acceso a qué KPIs
          </p>
        </div>

        {/* View Toggle */}
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'users' ? 'default' : 'outline'}
            onClick={() => setViewMode('users')}
            data-testid="view-by-users-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            Ver por Usuario
          </Button>
          <Button
            variant={viewMode === 'kpis' ? 'default' : 'outline'}
            onClick={() => setViewMode('kpis')}
            data-testid="view-by-kpis-btn"
          >
            <Target className="w-4 h-4 mr-2" />
            Ver por KPI
          </Button>
        </div>

        {/* Users View */}
        {viewMode === 'users' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Permisos por Usuario
              </CardTitle>
              <CardDescription>
                Lista de usuarios y los KPIs a los que tienen acceso
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Equipos</TableHead>
                      <TableHead>KPIs Accesibles</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => {
                      const userTeams = getUserTeams(user.id);
                      const accessibleKpis = getUserKpis(user);
                      
                      return (
                        <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-slate-600">
                                  {user.name?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{user.name}</p>
                                <p className="text-xs text-slate-500">{user.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getRoleBadge(user.role)}</TableCell>
                          <TableCell>
                            {user.role === 'admin' ? (
                              <Badge variant="outline" className="text-xs">Todos</Badge>
                            ) : userTeams.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {userTeams.slice(0, 2).map(team => (
                                  <Badge key={team.id} variant="outline" className="text-xs">
                                    {team.name}
                                  </Badge>
                                ))}
                                {userTeams.length > 2 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{userTeams.length - 2}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">Sin equipos</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {user.role === 'admin' ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Acceso total</Badge>
                            ) : accessibleKpis.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {accessibleKpis.slice(0, 2).map(kpi => (
                                  <Badge key={kpi.id} variant="outline" className="text-xs uppercase">
                                    {kpi.name.length > 15 ? kpi.name.slice(0, 15) + '...' : kpi.name}
                                  </Badge>
                                ))}
                                {accessibleKpis.length > 2 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{accessibleKpis.length - 2}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Sin acceso
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge className={cn(
                              user.role === 'admin' ? 'bg-emerald-100 text-emerald-700' :
                              accessibleKpis.length > 0 ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-500'
                            )}>
                              {user.role === 'admin' ? kpis.length : accessibleKpis.length}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPIs View */}
        {viewMode === 'kpis' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Permisos por KPI
              </CardTitle>
              <CardDescription>
                Lista de KPIs y los usuarios que tienen acceso
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>KPI</TableHead>
                      <TableHead>Área</TableHead>
                      <TableHead>Equipos</TableHead>
                      <TableHead>Usuarios con Acceso</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kpis.map((kpi) => {
                      const kpiTeams = getKpiTeams(kpi);
                      const kpiUsers = getKpiUsers(kpi);
                      const admins = kpiUsers.filter(u => u.accessType === 'admin');
                      const nonAdmins = kpiUsers.filter(u => u.accessType !== 'admin');
                      
                      return (
                        <TableRow key={kpi.id} data-testid={`kpi-row-${kpi.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                                <Target className="w-4 h-4 text-emerald-600" />
                              </div>
                              <span className="font-medium uppercase">{kpi.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-slate-600 uppercase">{kpi.area}</span>
                          </TableCell>
                          <TableCell>
                            {kpiTeams.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {kpiTeams.slice(0, 2).map(team => (
                                  <Badge key={team.id} variant="outline" className="text-xs">
                                    {team.name}
                                  </Badge>
                                ))}
                                {kpiTeams.length > 2 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{kpiTeams.length - 2}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-amber-600 text-sm flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Sin equipos
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {admins.length > 0 && (
                                <Badge className="bg-red-100 text-red-700 text-xs">
                                  {admins.length} Admin{admins.length > 1 ? 's' : ''}
                                </Badge>
                              )}
                              {nonAdmins.slice(0, 2).map(({ user, accessType }) => (
                                <Badge 
                                  key={user.id} 
                                  variant="outline" 
                                  className={cn(
                                    "text-xs",
                                    accessType === 'assigned' && "bg-purple-50 border-purple-200"
                                  )}
                                >
                                  {user.name}
                                  {accessType === 'assigned' && ' (asignado)'}
                                </Badge>
                              ))}
                              {nonAdmins.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{nonAdmins.length - 2}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge className={cn(
                              kpiUsers.length > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                            )}>
                              {kpiUsers.length}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Key className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Cómo funcionan los permisos</h4>
                <ul className="text-sm text-blue-700 mt-2 space-y-1">
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3" />
                    <strong>Administradores:</strong> Acceso total a todos los KPIs
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3" />
                    <strong>Operadores:</strong> Acceso a KPIs de sus equipos asignados
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3" />
                    <strong>Viewers:</strong> Acceso solo a KPIs asignados individualmente
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Permissions;
